// Flight-planning data fetch (§8.4) — browser-direct, no proxy (both sources send CORS headers).
// SHGM drone zones + NOTAMs come from Kapadokya Üniversitesi's PUBLIC ArcGIS (no auth); weather from
// Open-Meteo (no key). Parsing/normalization is the shared engine (engine.ts).

import { parseEsriFeatures, parseNotamFeatures, fromOpenMeteo, type Zone, type Notam, type Weather } from './engine.ts';

const ARCGIS = 'https://arcgis.kapadokya.edu.tr/arcgis/rest/services/IHA_ILILCE/MapServer';
const OPEN_METEO = 'https://api.open-meteo.com/v1/forecast';

/**
 * Optional origin serving a PRE-BAKED snapshot of the two country-wide datasets, in the same Esri
 * FeatureService shape the live service returns — so `parseEsriFeatures` / `parseNotamFeatures` are
 * unchanged. `<origin>/manifest.json` names the current files; nothing else there has a stable name.
 *
 * Unset (the default, and what drone-player runs with) means the whole-country calls paginate the
 * live ArcGIS exactly as before. The public site sets it, for three reasons: a public map would put
 * thousands of visitors' worth of load on a university's server that never agreed to carry it; a
 * static object behind a CDN is a great deal faster than 6 paginated queries; and it removes a
 * hard runtime dependency on a third party with no contract and no SLA.
 *
 * Only the two country-wide calls honour it. The per-viewport `fetchZones`/`fetchNotams` still hit
 * the live service, because a bbox query has no snapshot equivalent and they are not on the public
 * site's path.
 */
const SNAPSHOT: string | undefined =
  (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_ZONE_SNAPSHOT || undefined;

interface SnapshotManifest {
  /** When the bake ran, epoch ms, or null when the manifest does not say. */
  bakedAt: number | null;
  /** Version-stamped filename per layer. Absent means that layer has nothing publishable. */
  snapshots: Partial<Record<'zones' | 'notams', string>>;
  /** Version-stamped place-search index, or null when this origin publishes none. */
  placesFile: string | null;
}

/**
 * Coalescing window for the manifest read. It is deliberately EQUAL to the manifest's own
 * `max-age`, and both halves of that matter.
 *
 * Long enough: one refresh asks for zones, NOTAMs and the bake time concurrently, and all three
 * must come from the SAME manifest read. Otherwise a refresh that straddles a bake can pair one
 * bake's timestamp with another bake's data and display an age belonging to neither.
 *
 * Short enough: the memo must not outlive one refresh, or the twelve-hourly background refresh
 * would keep re-reading the version it first saw and never observe a new bake at all.
 */
const MANIFEST_MEMO_MS = 60_000;
let manifestMemo: { at: number; p: Promise<SnapshotManifest> } | null = null;

/**
 * No AbortSignal, deliberately.
 *
 * This promise is SHARED for a minute, so binding it to the first caller's signal let one caller's
 * abort reject an answer a different caller was waiting on. The window is not theoretical: the
 * twelve-hourly refresh and a visitor pressing ⟳ both call `loadData`, which aborts the previous
 * controller and then synchronously starts the new fetch — before the `.catch` that clears the memo
 * has run. The second caller attached to an already-rejected promise and failed with `AbortError`,
 * which on a first visit is the red "this map is NOT reliable" banner over an empty map.
 *
 * The object is 723 bytes. Letting an abandoned read finish costs nothing worth this failure mode;
 * callers that care about their own abort check `signal.aborted` after awaiting.
 */
function loadManifest(): Promise<SnapshotManifest> {
  if (manifestMemo && Date.now() - manifestMemo.at < MANIFEST_MEMO_MS) return manifestMemo.p;
  const p = (async (): Promise<SnapshotManifest> => {
    const r = await fetch(`${SNAPSHOT}/manifest.json`, { cache: 'no-cache' });
    if (!r.ok) throw new Error(`snapshot manifest: HTTP ${r.status}`);
    const m = (await r.json()) as {
      bakedAt?: unknown;
      layers?: Record<string, { snapshot?: unknown } | undefined>;
      places?: { file?: unknown };
    };
    const t = typeof m.bakedAt === 'string' ? Date.parse(m.bakedAt) : NaN;
    const snapshots: SnapshotManifest['snapshots'] = {};
    for (const name of ['zones', 'notams'] as const) {
      const f = m.layers?.[name]?.snapshot;
      if (typeof f === 'string' && f) snapshots[name] = f;
    }
    const pf = m.places?.file;
    return { bakedAt: Number.isFinite(t) ? t : null, snapshots, placesFile: typeof pf === 'string' && pf ? pf : null };
  })().catch((e: unknown) => {
    manifestMemo = null; // a failed read is not an answer worth remembering
    throw e;
  });
  manifestMemo = { at: Date.now(), p };
  return p;
}

/**
 * A country-wide dataset, by the name the CURRENT manifest gives it.
 *
 * The filename is never constructed here. Both the file and the bake timestamp come from one
 * manifest read, so the dataset a visitor holds and the age shown next to it are the same bake by
 * construction rather than by timing. A manifest that names no snapshot for a layer is an error and
 * not an invitation to guess a stable name — an object of unknown age is exactly what the freshness
 * rule exists to keep off the map.
 */
async function fetchSnapshot(name: 'zones' | 'notams', signal?: AbortSignal): Promise<unknown> {
  const file = (await loadManifest()).snapshots[name];
  // The shared manifest read ignores this signal (see above); the caller's abort is honoured here
  // instead, before the part that is actually worth aborting — a multi-megabyte snapshot.
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
  if (!file) throw new Error(`SHGM ${name}: the published manifest names no snapshot for this layer`);
  const r = await fetch(`${SNAPSHOT}/${file}`, { signal });
  if (!r.ok) throw new Error(`SHGM ${name} snapshot: HTTP ${r.status}`);
  return r.json();
}

/** True when this build reads pre-baked snapshots rather than the live service. */
export const usingSnapshot = (): boolean => !!SNAPSHOT;

/**
 * When the snapshot's DATA was produced, in epoch ms — not when this browser downloaded it.
 *
 * The distinction is the whole point. Against the live service those two times are the same and
 * either can be shown to a user. Against a snapshot they are unrelated: a browser can download a
 * three-week-old file in 200 ms, and stamping the download time onto it tells the user the airspace
 * data is current when it is not. Returns null when the age cannot be established, which callers
 * must treat as "unknown", never as "fresh".
 */
export async function fetchSnapshotBakedAt(_signal?: AbortSignal): Promise<number | null> {
  return (await fetchSnapshotIdentity()).bakedAt;
}

/** Which published bake a client is holding, and when it was made. */
export interface SnapshotIdentity {
  bakedAt: number | null;
  /**
   * The objects this dataset is built from, as one comparable string, or null when that cannot be
   * established. Comparing it against a cached copy answers "has a new bake been published?" for
   * 723 bytes, instead of by refetching several megabytes to find out nothing changed.
   */
  key: string | null;
}

export async function fetchSnapshotIdentity(): Promise<SnapshotIdentity> {
  if (!SNAPSHOT) return { bakedAt: null, key: null };
  try {
    const m = await loadManifest();
    const z = m.snapshots.zones;
    const n = m.snapshots.notams;
    return { bakedAt: m.bakedAt, key: z && n ? `${z}|${n}` : null };
  } catch {
    return { bakedAt: null, key: null }; // unreachable or malformed manifest → unknown, not fresh
  }
}

/** Filename of the baked place-search index, or null when this build has none (see geocode.ts). */
export async function fetchPlaceIndexFile(_signal?: AbortSignal): Promise<string | null> {
  if (!SNAPSHOT) return null;
  try {
    return (await loadManifest()).placesFile;
  } catch {
    return null; // no index → the caller falls back to its geocoder
  }
}

export interface BBox {
  minLon: number;
  minLat: number;
  maxLon: number;
  maxLat: number;
}

/** A square-ish bbox of ±`halfDeg` around a point (~0.15° ≈ 15 km at Turkish latitudes). */
export function bboxAround(lat: number, lon: number, halfDeg = 0.15): BBox {
  return { minLon: lon - halfDeg, minLat: lat - halfDeg, maxLon: lon + halfDeg, maxLat: lat + halfDeg };
}

async function arcgisQuery(layer: number, bbox: BBox, outFields: string, signal?: AbortSignal): Promise<unknown> {
  const params = new URLSearchParams({
    f: 'json',
    where: '1=1',
    geometry: `${bbox.minLon},${bbox.minLat},${bbox.maxLon},${bbox.maxLat}`,
    geometryType: 'esriGeometryEnvelope',
    inSR: '4326',
    outSR: '4326',
    spatialRel: 'esriSpatialRelIntersects',
    outFields,
    returnGeometry: 'true',
  });
  const r = await fetch(`${ARCGIS}/${layer}/query?${params}`, { signal });
  if (!r.ok) throw new Error(`SHGM ArcGIS layer ${layer}: HTTP ${r.status}`);
  return r.json();
}

/** Permanent drone zones (layer 2) intersecting the bbox. AreaType 909 = no-fly, 908 = allowed. */
export async function fetchZones(bbox: BBox, signal?: AbortSignal): Promise<Zone[]> {
  return parseEsriFeatures(await arcgisQuery(2, bbox, 'OBJECTID,Name,ActorID,AreaType,Authority,LocationID', signal));
}

/** Temporary NOTAM areas (layer 3) intersecting the bbox. Filter to in-force with activeNotamsAt. */
export async function fetchNotams(bbox: BBox, signal?: AbortSignal): Promise<Notam[]> {
  return parseNotamFeatures(await arcgisQuery(3, bbox, '*', signal));
}

/** Every drone zone in the country (layer 2), paginated. ~11k features / ~23 MB — call once and cache
 *  (see zoneStore.ts), don't refetch per view. `onProgress` reports the running feature count. */
/** Run tasks with a bounded concurrency (don't fire 30 requests at the SHGM server at once). */
async function pool<T>(tasks: Array<() => Promise<T>>, limit: number): Promise<T[]> {
  const out: T[] = new Array(tasks.length);
  let i = 0;
  const worker = async (): Promise<void> => {
    while (i < tasks.length) {
      const idx = i++;
      out[idx] = await tasks[idx]();
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, tasks.length) }, worker));
  return out;
}

export async function fetchAllZones(signal?: AbortSignal, onProgress?: (n: number) => void): Promise<Zone[]> {
  if (SNAPSHOT) {
    const zones = parseEsriFeatures(await fetchSnapshot('zones', signal));
    onProgress?.(zones.length);
    return zones;
  }
  const PAGE = 2000;
  // `geometryPrecision: 5` (~1 m) roughly halves the payload without removing vertices — coordinates are
  // just rounded (≤1 m, below GPS accuracy), so zone boundaries / the fly/no-fly assessment are unaffected.
  const base = { f: 'json', where: '1=1', outFields: 'OBJECTID,Name,ActorID,AreaType,Authority,LocationID', returnGeometry: 'true', outSR: '4326', geometryPrecision: '5' };
  const fetchPage = async (offset: number): Promise<unknown[]> => {
    const params = new URLSearchParams({ ...base, resultOffset: String(offset), resultRecordCount: String(PAGE) });
    const r = await fetch(`${ARCGIS}/2/query?${params}`, { signal });
    if (!r.ok) throw new Error(`SHGM zones: HTTP ${r.status}`);
    return ((await r.json()) as { features?: unknown[] }).features ?? [];
  };
  // One cheap count query, then fetch every page in PARALLEL (was ~6 sequential round-trips).
  let total = 0;
  try {
    const cr = await fetch(`${ARCGIS}/2/query?f=json&where=1=1&returnCountOnly=true`, { signal });
    if (cr.ok) total = ((await cr.json()) as { count?: number }).count ?? 0;
  } catch {
    /* count unsupported → sequential fallback below */
  }
  let features: unknown[] = [];
  if (total > 0) {
    const pages = Math.min(Math.ceil(total / PAGE), 40); // cap = safety backstop
    features = (await pool(Array.from({ length: pages }, (_, p) => () => fetchPage(p * PAGE)), 8)).flat();
    onProgress?.(features.length);
  } else {
    for (let offset = 0; offset <= 50000; offset += PAGE) {
      const got = await fetchPage(offset);
      features.push(...got);
      onProgress?.(features.length);
      if (got.length < PAGE) break;
    }
  }
  return parseEsriFeatures({ features });
}

/** Every NOTAM area in the country (layer 3) — ~1.3k, one call. */
export async function fetchAllNotams(signal?: AbortSignal): Promise<Notam[]> {
  if (SNAPSHOT) return parseNotamFeatures(await fetchSnapshot('notams', signal));
  const params = new URLSearchParams({ f: 'json', where: '1=1', outFields: '*', returnGeometry: 'true', outSR: '4326', geometryPrecision: '5' });
  const r = await fetch(`${ARCGIS}/3/query?${params}`, { signal });
  if (!r.ok) throw new Error(`SHGM NOTAM: HTTP ${r.status}`);
  return parseNotamFeatures(await r.json());
}

export interface WeatherAtPoint {
  weather: Weather;
  elevation: number | null; // ground elevation (m), from the forecast response
  temperatureC: number | null;
}

/** Current weather + ground elevation at the point (Open-Meteo, no key). Wind normalized to m/s. */
export async function fetchWeather(lat: number, lon: number, signal?: AbortSignal): Promise<WeatherAtPoint | null> {
  const params = new URLSearchParams({
    latitude: String(lat),
    longitude: String(lon),
    current: 'wind_speed_10m,wind_gusts_10m,precipitation,visibility,temperature_2m',
    wind_speed_unit: 'ms', // engine also normalizes, but ask for m/s directly
  });
  const r = await fetch(`${OPEN_METEO}?${params}`, { signal });
  if (!r.ok) throw new Error(`Open-Meteo: HTTP ${r.status}`);
  const j = (await r.json()) as { elevation?: number; current?: { temperature_2m?: number } };
  const weather = fromOpenMeteo(j);
  if (!weather) return null;
  return {
    weather,
    elevation: typeof j.elevation === 'number' ? j.elevation : null,
    temperatureC: typeof j.current?.temperature_2m === 'number' ? j.current.temperature_2m : null,
  };
}
